import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";
import PlatformRatings from "./PlatformRatings";
import { Button, Loader, Paper, Stack, Text } from "@mantine/core";
import Tutorial from './Tutorial';

const Notifications = ( {unreadCountCallback} ) => {
  const myUser = useContext(MyUserContext);

  const [notifications, setNotifications] = useState(null);
  const [unreadCount, setUnreadCount] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchNotifications = async () => {
    try {
      const firstParams = new URLSearchParams();
      firstParams.set('count', '10');
      const firstResponse = await axiosInstance.get(`/notifications?${firstParams.toString()}`);
      setNotifications(firstResponse.data);

      const secondParams = new URLSearchParams();
      secondParams.set('count', '10000');
      secondParams.set('offset', '10');
      setIsLoadingMore(true);
      const secondResponse = await axiosInstance.get(`/notifications?${secondParams.toString()}`);
      setIsLoadingMore(false);
      if (Array.isArray(secondResponse.data) && secondResponse.data.length > 0) {
        setNotifications((prev) => (prev || []).concat(secondResponse.data));
      }
    } catch (error) {
      setIsLoadingMore(false);
      console.error(error);
    }
  };

  const markAsRead = (id) => {
    axiosInstance
    .post(`/notifications/${id}/markAsRead`)
    .then((response) => {
      const newUnreadCount = Math.max(0, unreadCount - 1);
      unreadCountCallback(newUnreadCount);
      setUnreadCount(newUnreadCount);
      fetchNotifications();
    })
    .catch((error) => {
      console.error('Failed to mark notification as read:', error);
    })
  };

  const markAsReadSilent = (id) => {
    axiosInstance.post(`/notifications/${id}/markAsRead`).catch(() => {});
  };

  const autoReadTimers = useRef({});
  const autoReadMarked = useRef(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.dataset.notificationId;
        if (!id) return;
        if (entry.isIntersecting) {
          if (!autoReadTimers.current[id] && !autoReadMarked.current.has(id)) {
            autoReadTimers.current[id] = setTimeout(() => {
              autoReadMarked.current.add(id);
              markAsReadSilent(id);
              delete autoReadTimers.current[id];
            }, 2000);
          }
        } else {
          if (autoReadTimers.current[id]) {
            clearTimeout(autoReadTimers.current[id]);
            delete autoReadTimers.current[id];
          }
        }
      });
    }, { threshold: 1.0 });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      Object.values(autoReadTimers.current).forEach(clearTimeout);
    };
  }, []);

  const observeNotification = useCallback((el) => {
    if (el && observerRef.current) {
      observerRef.current.observe(el);
    }
  }, []);

  const fetchUnreadCount = () => {
    axiosInstance
    .get('/notifications/unreadCount')
    .then((response) => {
      setUnreadCount(response.data.unreadCount);
      unreadCountCallback(response.data.unreadCount);
    })
    .catch((error) => {
      console.error('Failed to fetch unread notification count:', error);
    })
  };

  const parseStringToBold = (text) => {
    const parts = text.split(/(\*[^*]+\*)/);

    return parts.map((part, index) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <strong key={index}>{part.slice(1, -1)}</strong>;
      } else {
        return part;
      }
    });
  };

  const groupNotifications = (notifications) => {
    const GROUP_THRESHOLD = 3;

    const grouped = [];
    let currentGroup = [];

    notifications.forEach((notification, index) => {
      if (
        currentGroup.length > 0 &&
        currentGroup[0].initiatingUserId === notification.initiatingUserId &&
        currentGroup[0].kind === notification.kind
      ) {
        currentGroup.push(notification);
      } else {
        if (currentGroup.length > 0) {
          if (currentGroup.length < GROUP_THRESHOLD) {
            grouped.push(...currentGroup.map((item) => [item]));
          } else {
            grouped.push(currentGroup);
          }
        }
        currentGroup = [notification];
      }
    });

    if (currentGroup.length > 0) {
      if (currentGroup.length < GROUP_THRESHOLD) {
        grouped.push(...currentGroup.map((item) => [item]));
      } else {
        grouped.push(currentGroup);
      }
    }

    return grouped;
  };

  const notificationTitle = (notification) => {
    const baseTitle = notification.title;
    const isFinished = notification.notification && notification.notification.finished === true;
    if (!isFinished) {
      return baseTitle;
    }
    if (notification.kind === "user.watched.something") {
      return language === "ru" ? "посмотрел(а) фильм" : "finished the movie";
    }
    if (notification.kind === "user.read.something") {
      return language === "ru" ? "прочитал(а) книгу" : "finished the book";
    }
    if (notification.kind === "user.played.something") {
      return language === "ru" ? "прошел(а) игру" : "finished the game";
    }
    return baseTitle;
  };

  const NotificationCard = ({ notificationGroup }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const firstNotification = notificationGroup[0];
    const ntfText = notificationTitle(firstNotification);
    const link = firstNotification.linkToEvent;

    const handleNotificationClick = (notification) => {
      markAsRead(notification.notification.id);
      navigate(notification.linkToEvent);
    };

    const localizedShowMore = (count) => {
      return language === "ru" ? `Показать еще ${count}` : `Show ${count} more`;
    };

    const localizedHide = () => {
      return language === "ru" ? "Скрыть" : "Hide";
    };

    const renderEventTags = (tags) => {
      if (!tags || tags.length === 0) {
        return null;
      }

      return (
        <div className="mt-1 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <TagBadge
              key={tag.id}
              size="xs"
              radius="sm"
              variant="filled"
              color={tagBadgeColor(tag.name, tag.type)}
            >
              {tagName(tag.name, language)}
            </TagBadge>
          ))}
        </div>
      );
    };

    const renderEventMessages = (messages) => {
      if (!messages || messages.length === 0) {
        return null;
      }

      return (
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <Text size="xs" tt="uppercase" c="dimmed" mb={4} style={{ letterSpacing: '0.05em' }}>
            {language === "ru" ? "Сообщения из чата" : "Chat messages"}
          </Text>
          <div className="flex flex-col gap-2">
          {[...messages].reverse().map((message, index) => (
            <div key={index} className="flex items-start gap-2">
              <img className="w-6 h-6 rounded-full" src={message.userAvatar} />
              <div className="flex-1 leading-snug">
                <Text span size="xs" fw={600} mr={4}>{message.userName}</Text>
                <Text span size="xs" c="dimmed">{message.text}</Text>
              </div>
            </div>
          ))}
          </div>
        </div>
      );
    };

    const renderArtTitle = (notification) => {
      if (!notification.artTitle) {
        return null;
      }
      const yearPart = notification.artYear ? ` (${notification.artYear})` : "";
      return (
        <Text size="sm" fw={600}>
          {notification.artTitle}
          {yearPart}
        </Text>
      );
    };

    const renderLastLogbookTime = (notification) => {
      if (!notification.lastLogbookTime || !notification.lastLogbookLabel) {
        return null;
      }
      const formatted = new Date(notification.lastLogbookTime).toLocaleDateString(language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return (
        <Text size="xs" c="dimmed" mt={2}>
          {notification.lastLogbookLabel}: {formatted}
        </Text>
      );
    };

    const renderDescriptionPreview = (description) => {
      if (!description) {
        return null;
      }
      const paragraphs = description.split(/\r?\n/);
      const firstParagraph = paragraphs.find((p) => p.trim() !== "");
      if (!firstParagraph) {
        return null;
      }
      const sentences = firstParagraph.match(/[^.!?]+[.!?]+/g) || [firstParagraph];
      const preview = sentences.join(" ").trim();
      const limit = 150;
      const trimmed = preview.length > limit ? preview.slice(0, limit).trim() + "..." : preview;

      return (
        <Text size="xs" c="dimmed" mt={2}>
          {trimmed}
        </Text>
      );
    };

    const renderSingleNotification = (notification, isFirst) => {
      const isUnread = notification.notification.read === false;
      const text = isFirst ? ntfText : notificationTitle(notification);
      const boldText = notification.initiatingUserName
        ? "*" + notification.initiatingUserName + "* " + text
        : "*" + text + "*";

      return (
        <Paper
          ref={isUnread ? observeNotification : undefined}
          data-notification-id={isUnread ? notification.notification.id : undefined}
          shadow={isUnread ? "sm" : "xs"}
          radius="lg"
          p="sm"
          className="cursor-pointer hover:shadow-md"
          style={{ backgroundColor: isUnread ? 'var(--app-unread-bg)' : 'transparent' }}
          onClick={() => handleNotificationClick(notification)}
        >
          <div className="flex gap-3 items-start">
            <div className="flex-shrink-0">
              <img
                className="rounded-lg w-[72px] h-[96px] object-cover"
                src={notification.artImageUrl}
                onClick={(e) => {
                  e.stopPropagation();
                  const artLink = notification.linkToArt || (notification.notification?.artEventId && `/artEvents/${notification.notification.artEventId}`);
                  if (artLink) {
                    navigate(artLink);
                  }
                }}
                style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              />
              {notification.otherPlatformInfos && notification.otherPlatformInfos.length > 0 && (
                <div className="mt-1">
                  <PlatformRatings art={{ otherPlatformInfos: notification.otherPlatformInfos }} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {notification.initiatingUserName ? (
                  <>
                    <img
                      className="w-8 h-8 rounded-full flex-shrink-0"
                      src={notification.initiatingUserAvatarUrl}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (notification.initiatingUserId) {
                          navigate(`/users/${notification.initiatingUserId}/likes`);
                        }
                      }}
                      style={{ cursor: 'pointer', transition: 'transform 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                    />
                    <Text size="sm">{parseStringToBold(boldText)}</Text>
                  </>
                ) : (
                  <Text size="sm">{parseStringToBold(boldText)}</Text>
                )}
              </div>
              {renderArtTitle(notification)}
              {renderDescriptionPreview(notification.artDescription)}
              {renderLastLogbookTime(notification)}
              {renderEventTags(notification.eventTags)}
              {renderEventMessages(notification.eventMessages)}
            </div>
          </div>
        </Paper>
      );
    };

    return (
      <div>
        {renderSingleNotification(firstNotification, true)}
        {notificationGroup.length > 1 && (
          <div className="ml-4 mt-1">
            <Button variant="subtle" size="xs" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? localizedHide() : localizedShowMore(notificationGroup.length - 1)}
            </Button>
            {isExpanded && (
              <Stack gap="xs" mt="xs">
                {notificationGroup.slice(1).map((notification) => (
                  <div key={notification.notification.id}>
                    {renderSingleNotification(notification, false)}
                  </div>
                ))}
              </Stack>
            )}
          </div>
        )}
      </div>
    );
  };

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  if (!myUser) {
    return (
      <div className="flex mt-2 md:mt-4 justify-center items-center text-center">
        <Text c="dimmed">
          {language === "ru" ? "Здесь будут уведомления" : "Here will be notifications"}
        </Text>
      </div>
    );
  }

  if (notifications === null && unreadCount === null) {
    fetchNotifications();
    fetchUnreadCount();
  }
  if (notifications === null || unreadCount === null) {
    return (
      <div></div>
    )
  }

  const groupedNotifications = notifications.map((notification) => [notification]);

  const showTutorial = myUser?.myUserInfo?.finishedTutorial === false;

  return (
    <div className="container mx-auto w-full lg:w-1/2">
      {showTutorial && <Tutorial language={language} />}
      {
      (groupedNotifications && groupedNotifications.length == 0) &&
      <div className="flex mt-4 justify-center items-center text-center">
        <Text c="dimmed" ta="center">
          {language === "ru"
            ? <>У вас пока нет уведомлений.<br/>Подписывайтесь на других пользователей, чтобы получать их.</>
            : <>No notifications yet.<br/>Subscribe to other users to receive notifications.</>
          }
        </Text>
      </div>
      }

      {
      (groupedNotifications && groupedNotifications.length > 0) &&
      <Stack gap="sm" mt="sm">
        {groupedNotifications.map((notificationGroup, index) => (
          <div key={index}>
            <NotificationCard notificationGroup={notificationGroup}/>
          </div>
        ))}
      </Stack>
      }
      {isLoadingMore && (
        <div className="flex justify-center mt-4">
          <Loader size="sm" />
        </div>
      )}
    </div>
  );
};

export default Notifications;
